import { IonAlert, IonButton } from '@ionic/react';
import RCForm, { useForm } from 'rc-field-form';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import Field from '@components/Form/Field';
import InputField from '@components/Form/InputField';
import Password from '@components/Form/Password';
import KioskLayout, { LockMode } from '@components/KioskLayout';
import useLogin, { LoginType } from '@hooks/useLogin';
import { getIsOnline } from '@state/system';

const Button = styled(IonButton)`
  width: 100%;
  padding: 0 16px;
  margin-top: 32px;
`;

const Description = styled.h6`
  margin: 37px 0 72px;
`;

const Form = styled(RCForm)`
  width: 100%;
`;

type Props = {
  type: LoginType;
};

const Unlock = ({ type }: Props) => {
  const [form] = useForm();
  const { validateFields } = form;
  const isOnline = useSelector(getIsOnline);
  const [credential, setCredential] = useState();
  const { loading: submitting, error } = useLogin(type, credential);
  const isDisabled = !isOnline || submitting;

  const onFinish = async () => {
    const values = await validateFields();
    setCredential(values);
  };

  return (
    <KioskLayout lockMode={LockMode.Unlock}>
      <Form
        // skipcq: JS-0417
        onFinish={onFinish}
        form={form}
        initialValues={{ email: '', password: '' }}
      >
        <h1>{t`Unlock Device`}</h1>
        <Description>{t`Enter your credentials to unlock the device and close Kiosk Mode.`}</Description>
        <Field
          name="email"
          rules={[
            { required: true, message: t`Email Address is required.` },
            { type: 'email', message: t`Invalid email address.` },
          ]}
        >
          <InputField
            label={t`Email Address`}
            inputMode="email"
            role="textbox"
            aria-label="email"
            data-testid="email"
          />
        </Field>
        {type === LoginType.WithPassword && (
          <Field name="password" rules={[{ required: true, message: t`Password is required.` }]}>
            <Password label={t`Password`} />
          </Field>
        )}
        {type === LoginType.WithAccessCode && (
          <Field
            name="accessCode"
            rules={[{ required: true, message: t`Access Code is required.` }]}
          >
            <Password label={t`Access Code`} />
          </Field>
        )}
        <Button
          type="submit"
          data-testid="login"
          role="button"
          disabled={isDisabled}
        >{t`Log In`}</Button>

        <IonAlert
          isOpen={error}
          header={t`Incorrect Login`}
          message={t`Check your login information and try again.`}
          buttons={[t`OK`]}
        />
      </Form>
    </KioskLayout>
  );
};

export default Unlock;
